import React, { useState, useEffect } from 'react';
import { api } from '../../services/apiClient';
import { theme } from '../../styles/theme';
import Button from '../ui/Button';
import Card from '../ui/Card';

const DashboardAddresses = ({ showSuccess, showError, showConfirm }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAddress, setEditingAddress] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.addresses.list();
      setAddresses(data || []);
    } catch (err) {
      setError('Failed to load addresses: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const addressData = {
      label: formData.get('label'),
      recipient_name: formData.get('recipient_name'),
      full_name: formData.get('recipient_name'),
      street1: formData.get('street1'),
      street2: formData.get('street2') || '',
      address_line1: formData.get('street1'),
      address_line2: formData.get('street2') || '',
      city: formData.get('city'),
      state: formData.get('state'),
      postal_code: formData.get('postal_code'),
      country: formData.get('country') || 'US',
      phone: formData.get('phone'),
      is_default: formData.get('is_default') === 'on',
    };

    try {
      setLoading(true);
      if (editingAddress?.id) {
        await api.addresses.update(editingAddress.id, addressData);
      } else {
        await api.addresses.create(addressData);
      }
      setEditingAddress(null);
      await fetchAddresses();
      showSuccess(editingAddress?.id ? 'Address updated successfully' : 'Address added successfully');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMsg = 'Failed to save address';
      if (Array.isArray(errorDetail)) {
        const errors = errorDetail.map(e => `${e.loc?.join('.') || 'Field'}: ${e.msg}`).join(', ');
        errorMsg += ': ' + errors;
      } else if (typeof errorDetail === 'string') {
        errorMsg += ': ' + errorDetail;
      } else {
        errorMsg += ': ' + err.message;
      }
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    await showConfirm({
      title: 'Delete Address',
      message: 'Are you sure you want to delete this address? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.addresses.delete(addressId);
          await fetchAddresses();
          showSuccess('Address deleted successfully!');
        } catch (err) {
          showError('Failed to delete address: ' + (err.response?.data?.detail || err.message));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const styles = {
    container: {
      width: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xl,
      gap: theme.spacing.md,
      flexWrap: 'wrap',
    },
    title: {
      fontSize: theme.typography.fontSize['3xl'],
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      margin: 0,
    },
    formContainer: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    formSubtitle: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      margin: `${theme.spacing.xs} 0 0 0`,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formSection: {
      padding: theme.spacing.xl,
      borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    formSectionLast: {
      padding: theme.spacing.xl,
    },
    sectionHeader: {
      marginBottom: theme.spacing.lg,
    },
    sectionTitle: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      margin: 0,
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.secondary,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    sectionDescription: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      margin: 0,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    formGroupFull: {
      gridColumn: '1 / -1',
    },
    label: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily.secondary,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
    labelHint: {
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.normal,
      color: theme.colors.textLight,
      fontStyle: 'italic',
    },
    input: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.secondary,
      border: `2px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      transition: `all ${theme.transitions.base}`,
      outline: 'none',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      backgroundColor: '#FFF8F0',
      borderRadius: theme.borderRadius.md,
      border: `2px solid ${theme.colors.borderLight}`,
      cursor: 'pointer',
      transition: `all ${theme.transitions.base}`,
      marginBottom: theme.spacing.lg,
    },
    checkboxContainerModern: {
      display: 'block',
      padding: theme.spacing.xl,
      backgroundColor: '#FFF8F0',
      borderRadius: theme.borderRadius.lg,
      border: `2px solid ${theme.colors.borderLight}`,
      cursor: 'pointer',
      transition: `all ${theme.transitions.base}`,
    },
    checkboxContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing.md,
    },
    checkbox: {
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      accentColor: theme.colors.primary,
      position: 'relative',
      zIndex: 1,
    },
    checkboxLabel: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.text,
      cursor: 'pointer',
      userSelect: 'none',
    },
    checkboxLabelModern: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    checkboxDescription: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formActions: {
      display: 'flex',
      gap: theme.spacing.md,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    },
    formActionsModern: {
      display: 'flex',
      gap: theme.spacing.md,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      padding: theme.spacing.xl,
      backgroundColor: '#F9F9F9',
      borderTop: `2px solid ${theme.colors.borderLight}`,
    },
    addressesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: theme.spacing.lg,
    },
    addressCard: {
      position: 'relative',
      padding: theme.spacing.xl,
      backgroundColor: theme.colors.surface,
      border: `2px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.lg,
      transition: `all ${theme.transitions.base}`,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    addressCardDefault: {
      borderColor: theme.colors.primary,
      backgroundColor: '#FFF8F0',
    },
    defaultBadge: {
      position: 'absolute',
      top: theme.spacing.md,
      right: theme.spacing.md,
      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
      backgroundColor: theme.colors.primary,
      color: theme.colors.textOnDark,
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.bold,
      borderRadius: theme.borderRadius.sm,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    addressLabel: {
      fontSize: theme.typography.fontSize.xl,
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      marginTop: 0,
    },
    addressName: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
    },
    addressDetails: {
      flex: 1,
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      lineHeight: theme.typography.lineHeight.relaxed,
      marginBottom: theme.spacing.lg,
    },
    addressLine: {
      margin: `${theme.spacing.xs} 0`,
    },
    addressPhone: {
      marginTop: theme.spacing.md,
      paddingTop: theme.spacing.md,
      borderTop: `1px solid ${theme.colors.borderLight}`,
    },
    cardActions: {
      display: 'flex',
      gap: theme.spacing.sm,
      marginTop: 'auto',
    },
    emptyState: {
      textAlign: 'center',
      padding: `${theme.spacing.xxl} ${theme.spacing.xl}`,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      border: `2px dashed ${theme.colors.borderLight}`,
    },
    emptyStateTitle: {
      fontSize: theme.typography.fontSize['2xl'],
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
    },
    emptyStateText: {
      fontSize: theme.typography.fontSize.base,
      color: theme.colors.textLight,
      marginBottom: theme.spacing.xl,
    },
    loadingState: {
      textAlign: 'center',
      padding: theme.spacing.xxl,
      fontSize: theme.typography.fontSize.lg,
      color: theme.colors.textLight,
    },
    errorMessage: {
      padding: theme.spacing.lg,
      backgroundColor: '#FEE',
      border: `2px solid ${theme.colors.error}`,
      borderRadius: theme.borderRadius.md,
      color: theme.colors.error,
      marginBottom: theme.spacing.lg,
      fontSize: theme.typography.fontSize.sm,
    },
  };

  const responsiveStyles = `
    .address-form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: ${theme.spacing.lg};
    }

    .address-form-full {
      grid-column: 1 / -1;
    }

    .address-input:focus {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 4px rgba(204, 85, 0, 0.1);
      transform: translateY(-1px);
    }

    .address-card-hover:hover {
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
      border-color: ${theme.colors.primary};
    }

    .checkbox-container:hover {
      background-color: #FFE8D6;
      border-color: ${theme.colors.primary};
    }

    .checkbox-container:hover .checkbox-icon,
    .checkbox-container input:checked ~ .checkbox-icon {
      opacity: 1;
      transform: scale(1.1);
    }

    @media (max-width: 768px) {
      .address-form-grid {
        grid-template-columns: 1fr;
        gap: ${theme.spacing.md};
      }

      .address-header {
        flex-direction: column;
        align-items: flex-start;
        gap: ${theme.spacing.md};
      }

      .address-header h2 {
        font-size: ${theme.typography.fontSize['2xl']};
      }

      .address-grid {
        grid-template-columns: 1fr;
        gap: ${theme.spacing.md};
      }

      .address-card-actions {
        flex-direction: column;
      }

      .address-card-actions button {
        width: 100%;
      }

      .form-section,
      .form-section-last,
      .form-actions-modern {
        padding: ${theme.spacing.lg};
      }
    }

    @media (max-width: 480px) {
      .address-form-actions {
        flex-direction: column-reverse;
      }

      .address-form-actions button {
        width: 100%;
      }

      .form-section,
      .form-section-last {
        padding: ${theme.spacing.md};
      }

      .form-actions-modern {
        padding: ${theme.spacing.md};
      }
    }
  `;

  if (loading && !addresses.length && !editingAddress) {
    return (
      <>
        <style>{responsiveStyles}</style>
        <div style={styles.loadingState}>
          <p>Loading addresses...</p>
        </div>
      </>
    );
  }

  if (editingAddress) {
    return (
      <>
        <style>{responsiveStyles}</style>
        <div style={styles.container}>
          <div style={styles.header} className="address-header">
            <div>
              <h2 style={styles.title}>{editingAddress.id ? 'Edit Address' : 'Add New Address'}</h2>
              <p style={styles.formSubtitle}>
                {editingAddress.id 
                  ? 'Update your shipping address details' 
                  : 'Add a new shipping address to your account'}
              </p>
            </div>
            <Button variant="outline" onClick={() => setEditingAddress(null)}>Cancel</Button>
          </div>

          <div style={styles.formContainer}>
            <Card variant="parchment" padding="none">
              <form onSubmit={handleAddressSubmit}>
                {/* Basic Information Section */}
                <div style={styles.formSection}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Basic Information</h3>
                    <p style={styles.sectionDescription}>Give this address a name and specify the recipient</p>
                  </div>
                  <div className="address-form-grid">
                    <div className="form-group">
                      <label style={styles.label}>
                        Address Label *
                        <span style={styles.labelHint}>e.g., Home, Office, Mom's House</span>
                      </label>
                      <input 
                        name="label" 
                        type="text" 
                        defaultValue={editingAddress.label} 
                        placeholder="Home" 
                        required 
                        style={styles.input}
                        className="address-input"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>
                        Recipient Name *
                        <span style={styles.labelHint}>Who will receive the package?</span>
                      </label>
                      <input 
                        name="recipient_name" 
                        type="text" 
                        defaultValue={editingAddress.full_name} 
                        placeholder="John Doe"
                        required 
                        style={styles.input}
                        className="address-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address Section */}
                <div style={styles.formSection}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Shipping Address</h3>
                    <p style={styles.sectionDescription}>Enter the complete delivery address</p>
                  </div>
                  <div className="address-form-grid">
                    <div className="form-group address-form-full">
                      <label style={styles.label}>Street Address *</label>
                      <input 
                        name="street1" 
                        type="text" 
                        defaultValue={editingAddress.address_line1} 
                        placeholder="123 Main Street"
                        required 
                        style={styles.input}
                        className="address-input"
                      />
                    </div>

                    <div className="form-group address-form-full">
                      <label style={styles.label}>
                        Apartment, Suite, etc.
                        <span style={styles.labelHint}>Optional</span>
                      </label>
                      <input 
                        name="street2" 
                        type="text" 
                        defaultValue={editingAddress.address_line2} 
                        placeholder="Apt 4B, Suite 200, Unit 5"
                        style={styles.input}
                        className="address-input"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>City *</label>
                      <input 
                        name="city" 
                        type="text" 
                        defaultValue={editingAddress.city} 
                        placeholder="New York"
                        required 
                        style={styles.input}
                        className="address-input"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>State / Province *</label>
                      <input 
                        name="state" 
                        type="text" 
                        defaultValue={editingAddress.state} 
                        placeholder="NY"
                        required 
                        style={styles.input}
                        className="address-input"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>Postal Code *</label>
                      <input 
                        name="postal_code" 
                        type="text" 
                        defaultValue={editingAddress.postal_code} 
                        placeholder="10001"
                        required 
                        style={styles.input}
                        className="address-input"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>Country *</label>
                      <input 
                        name="country" 
                        type="text" 
                        defaultValue={editingAddress.country || 'US'} 
                        placeholder="United States"
                        required 
                        style={styles.input}
                        className="address-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div style={styles.formSection}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Contact Information</h3>
                    <p style={styles.sectionDescription}>We'll use this to contact you about delivery</p>
                  </div>
                  <div className="address-form-grid">
                    <div className="form-group address-form-full">
                      <label style={styles.label}>
                        Phone Number *
                        <span style={styles.labelHint}>Required for delivery coordination</span>
                      </label>
                      <input 
                        name="phone" 
                        type="tel" 
                        defaultValue={editingAddress.phone} 
                        placeholder="(555) 123-4567"
                        required
                        style={styles.input}
                        className="address-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Default Address Toggle */}
                <div style={styles.formSectionLast}>
                  <label style={styles.checkboxContainerModern} className="checkbox-container">
                    <div style={styles.checkboxContent}>
                      <input 
                        name="is_default" 
                        type="checkbox" 
                        defaultChecked={editingAddress.is_default} 
                        style={styles.checkbox}
                      />
                      <div>
                        <div style={styles.checkboxLabelModern}>Set as default shipping address</div>
                        <div style={styles.checkboxDescription}>
                          This address will be automatically selected at checkout
                        </div>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Form Actions */}
                <div style={styles.formActionsModern} className="address-form-actions">
                  <Button variant="outline" onClick={() => setEditingAddress(null)} type="button" size="lg">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} size="lg">
                    {loading ? 'Saving...' : editingAddress.id ? 'Update Address' : 'Save Address'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={styles.container}>
          <div style={styles.header} className="address-header">
            <h2 style={styles.title}>Address Book</h2>
            <Button onClick={() => setEditingAddress({})}>Add New Address</Button>
          </div>

        {error && <div style={styles.errorMessage}>{error}</div>}

        {addresses.length === 0 ? (
          <div style={styles.emptyState}>
            <h3 style={styles.emptyStateTitle}>No Saved Addresses</h3>
            <p style={styles.emptyStateText}>Add an address to speed up your checkout process and make ordering easier.</p>
            <Button onClick={() => setEditingAddress({})}>Add Your First Address</Button>
          </div>
        ) : (
          <div style={styles.addressesGrid} className="address-grid">
            {addresses.map((address) => (
              <div 
                key={address.id} 
                style={{
                  ...styles.addressCard,
                  ...(address.is_default ? styles.addressCardDefault : {})
                }}
                className="address-card-hover"
              >
                {address.is_default && (
                  <span style={styles.defaultBadge}>Default</span>
                )}
                
                <h3 style={styles.addressLabel}>{address.label}</h3>
                <p style={styles.addressName}>{address.full_name}</p>
                
                <div style={styles.addressDetails}>
                  <p style={styles.addressLine}>{address.address_line1}</p>
                  {address.address_line2 && (
                    <p style={styles.addressLine}>{address.address_line2}</p>
                  )}
                  <p style={styles.addressLine}>
                    {address.city}, {address.state} {address.postal_code}
                  </p>
                  <p style={styles.addressLine}>{address.country}</p>
                  
                  {address.phone && (
                    <div style={styles.addressPhone}>
                      <span>Phone: {address.phone}</span>
                    </div>
                  )}
                </div>
                
                <div style={styles.cardActions} className="address-card-actions">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setEditingAddress(address)}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="danger" 
                    onClick={() => handleDeleteAddress(address.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardAddresses;
